import React, { useState } from "react";
import { Modal, Button, Upload, Input, Select, Alert, Progress, Space, Card, Checkbox, message } from "antd";
import { UploadOutlined, FileTextOutlined, FilePdfOutlined, FileWordOutlined, FileImageOutlined, DeleteOutlined } from "@ant-design/icons";
import { useNotifications } from "../../../../../context/NotificationContext";
import { getSessionUser } from "../../../../../utils/auth/sessionUser";

// Types
import { Test } from "../../../../../types/healthcare/TestTypes";
import { addTestResults } from "../../../../../api/healthcare/Testing/TestApi";
import { downscaleImage } from "../../../../../utils/downscaleImage";
import { User } from "../../../../../types/admin/UserTypes";

const { TextArea } = Input;

interface ResultUploadModalProps {
	isOpen: boolean;
	onClose: () => void;
	test: Test;
}

interface UploadedFile {
	file: File;
	name: string;
	size: number;
	type: string;
	preview?: string;
}

const ResultUploadModal: React.FC<ResultUploadModalProps> = ({ isOpen, onClose, test }) => {
	const user: User = getSessionUser();
	const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
	const [notes, setNotes] = useState<string>("");
	const [resultStatus, setResultStatus] = useState<string>("normal");
	const [uploading, setUploading] = useState<boolean>(false);
	const [uploadProgress, setUploadProgress] = useState<number>(0);
	const [performAnalysis, setPerformAnalysis] = useState<boolean>(false);

	const { showError, showSuccess } = useNotifications();

	const resultStatusOptions = [
		{ label: "Normal", value: "normal" },
		{ label: "Abnormal", value: "abnormal" },
		{ label: "Critical", value: "critical" },
		{ label: "Pending Review", value: "pending_review" },
	];

	const allowedFileTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];

	// Matches BloodTestAnalysisService::MAX_DOCUMENT_BYTES and xoe-agent's _MAX_DOCUMENT_BYTES.
	const maxFileSize = 5 * 1024 * 1024; // 5MB

	const invalidFilenameChars = /[^a-zA-Z0-9\s\-_\.()]/;

	const handleFileSelect = async (file: File) => {
		// Validate filename — special characters break storage path lookups
		if (invalidFilenameChars.test(file.name)) {
			showError(`Invalid filename: "${file.name}". Use only letters, numbers, spaces, hyphens, underscores, and dots.`);
			return false;
		}

		// Validate file type
		if (!allowedFileTypes.includes(file.type)) {
			showError(`Invalid file type: ${file.name}. Please upload PDF, JPG, or PNG files only.`);
			return false;
		}

		// Downscale first so the size check runs against the file that is actually sent.
		// The filename check above stays on the original: downscaling only ever swaps the
		// extension for .jpg, which cannot introduce an invalid character.
		const upload = await downscaleImage(file);

		// Validate file size
		if (upload.size > maxFileSize) {
			showError(`File too large: ${upload.name}. Maximum size is 5MB.`);
			return false;
		}

		setUploadedFiles((prev) => [
			...prev,
			{
				file: upload,
				name: upload.name,
				size: upload.size,
				type: upload.type,
			},
		]);

		return false; // Prevent automatic upload
	};

	const handleFileRemove = (index: number) => {
		setUploadedFiles((prev) => {
			const newFiles = [...prev];
			if (newFiles[index].preview) {
				URL.revokeObjectURL(newFiles[index].preview!);
			}
			newFiles.splice(index, 1);
			return newFiles;
		});
	};

	const handleSave = async () => {
		if (uploadedFiles.length === 0) {
			showError("Please upload at least one result document.");
			return;
		}

		setUploading(true);
		setUploadProgress(0);

		try {
			const formData = new FormData();

			uploadedFiles.forEach((uploadedFile, index) => {
				formData.append(`files[]`, uploadedFile.file);
			});

			formData.append("result_notes", notes);
			formData.append("is_abnormal", resultStatus === "abnormal" || resultStatus === "critical" ? "1" : "0");
			formData.append("requires_followup", resultStatus === "critical" || resultStatus === "pending_review" ? "1" : "0");
			formData.append(
				"results",
				JSON.stringify(
					uploadedFiles.map((file) => ({
						name: file.name,
						size: file.size,
						type: file.type,
					}))
				)
			);
			formData.append("result_id", test.sample_id || "");
			formData.append("perform_analysis", performAnalysis ? "1" : "0");
			formData.append("uploaded_by", user.id.toString());

			const progressInterval = setInterval(() => {
				setUploadProgress((prev) => {
					if (prev >= 90) {
						clearInterval(progressInterval);
						return prev;
					}
					return prev + 10;
				});
			}, 200);

			let result = await addTestResults(test.id, test.test_type[0] ,formData);

			clearInterval(progressInterval);
			setUploadProgress(100);

			// The collection count is not knowable here — it arrives with the practitioner's
			// notification once BloodTestAnalysisJob has read the report.
			showSuccess(`Successfully uploaded ${uploadedFiles.length} result document(s) — analysis is in progress. You will be notified when it finishes, including any earlier collections found in the same report.`);
			handleClose();
		} catch (error) {
			console.error("Upload error:", error);
			showError("Failed to upload result documents. Please try again.");
		} finally {
			setUploading(false);
		}
	};

	const handleClose = () => {
		uploadedFiles.forEach((file) => {
			if (file.preview) {
				URL.revokeObjectURL(file.preview);
			}
		});

		setUploadedFiles([]);
		setNotes("");
		setResultStatus("normal");
		setUploading(false);
		setUploadProgress(0);
		onClose();
	};

	const formatFileSize = (bytes: number): string => {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
	};

	const getFileIcon = (fileType: string) => {
		if (fileType.includes("pdf")) return <FilePdfOutlined style={{ fontSize: 24, color: "var(--mg-danger)" }} />;
		if (fileType.includes("image")) return <FileImageOutlined style={{ fontSize: 24, color: "var(--mg-success)" }} />;
		if (fileType.includes("word") || fileType.includes("document")) return <FileWordOutlined style={{ fontSize: 24, color: "var(--mg-primary)" }} />;
		return <FileTextOutlined style={{ fontSize: 24 }} />;
	};

	return (
		<Modal
			title={
				<Space>
					<UploadOutlined />
					<span>Upload Test Results</span>
				</Space>
			}
			open={isOpen}
			onCancel={handleClose}
			destroyOnClose
			footer={[
				<Button key="cancel" onClick={handleClose} disabled={uploading}>
					Cancel
				</Button>,
				<Button key="submit" type="primary" onClick={handleSave} loading={uploading} disabled={uploading}>
					{uploading ? "Uploading..." : "Upload Results"}
				</Button>,
			]}
			width={800}
			style={{ maxWidth: "90vw" }}
			closable={!uploading}
		>
			<Space direction="vertical" style={{ width: "100%" }} size="large">
				{uploading && (
					<div>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
							<span style={{ fontWeight: 500 }}>Uploading files...</span>
							<span style={{ fontSize: 14, opacity: 0.65 }}>{uploadProgress}%</span>
						</div>
						<Progress percent={uploadProgress} status="active" />
					</div>
				)}

				<div>
					<label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>
						Upload Result Documents <span style={{ color: "#ff4d4f" }}>*</span>
					</label>

					<Upload.Dragger accept=".pdf,.jpg,.jpeg,.png" beforeUpload={handleFileSelect} showUploadList={false} disabled={uploading}>
						<p className="ant-upload-drag-icon">
							<UploadOutlined />
						</p>
						<p className="ant-upload-text">Click or drag a PDF, JPG, or PNG file to this area to upload</p>
						<p className="ant-upload-hint">PDF, JPG, or PNG files. Maximum 5MB. Filename must contain only letters, numbers, spaces, hyphens, underscores, and dots.</p>
					</Upload.Dragger>

					<Alert message="Accepted formats: PDF, JPG, PNG. Maximum size: 5MB. No special characters in filename (e.g. * ? / \ : | &amp;)." type="info" showIcon style={{ marginTop: 12 }} />

					{uploadedFiles.length > 0 && (
						<Card size="small" title={`Selected Files (${uploadedFiles.length})`} style={{ marginTop: 16 }}>
							<Space direction="vertical" style={{ width: "100%" }} size="small">
								{uploadedFiles.map((file, index) => (
									<div
										key={index}
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "space-between",
											padding: 8,
											borderBottom: index < uploadedFiles.length - 1 ? "1px solid rgba(0,0,0,0.06)" : "none",
										}}
									>
										<Space>
											{getFileIcon(file.type)}
											<div>
												<div style={{ fontWeight: 500 }}>{file.name}</div>
												<div style={{ fontSize: 12, opacity: 0.65 }}>{formatFileSize(file.size)}</div>
											</div>
										</Space>
										<Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleFileRemove(index)} disabled={uploading} size="small" />
									</div>
								))}
							</Space>
						</Card>
					)}
				</div>

				{(test.test_category === "pathology" || test.test_category === "genetic_testing" || test.test_category === "laboratory" || test.test_category === "blood_tests") && (
					<div>
						<Checkbox checked={performAnalysis} onChange={(e) => setPerformAnalysis(e.target.checked)} disabled={uploading}>
							<span style={{ fontWeight: 500 }}>Perform Analysis</span>
						</Checkbox>
					</div>
				)}

				<div>
					<label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Result Status</label>
					<Select value={resultStatus} options={resultStatusOptions} onChange={setResultStatus} placeholder="Select result status" style={{ width: "100%" }} disabled={uploading} />
				</div>

				<div>
					<label style={{ display: "block", marginBottom: 8, fontWeight: 500 }}>Additional Notes</label>
					<TextArea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Enter any additional notes about the test results..." rows={4} disabled={uploading} />
				</div>
			</Space>
		</Modal>
	);
};

export default ResultUploadModal;
